import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useWarehouseLocal } from '@/hooks/useWarehouseLocal';
import { apiService } from '@/services/api';
import { StockChangeLogOut } from '@/types/api';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import { SearchableSelect } from '@/components/molecules/SearchableSelect';
import './WarehouseHistory.scss';

const PAGE_SIZE = 30;

const WarehouseHistory: React.FC = () => {
  const { t } = useTranslation();
  const { formatRelativeTime } = useDateFormatter();

  const { localId, setLocalId, availableLocals, loading: localLoading } = useWarehouseLocal();

  const [logs, setLogs] = useState<StockChangeLogOut[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setOffset(0);
    setLogs([]);
  }, [localId]);

  const loadHistory = useCallback(async () => {
    if (!localId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await apiService.getLocalStockHistory({ local_id: localId, limit: PAGE_SIZE, offset });
      setLogs((prev) => (offset === 0 ? data.logs : [...prev, ...data.logs]));
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to load warehouse history:', err);
      setError(t('errors.general'));
    } finally {
      setLoading(false);
    }
  }, [localId, offset, t]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const actionLabel = (actionType: StockChangeLogOut['action_type']) => {
    switch (actionType) {
      case 'add':
        return t('warehouse.history.actionAdd');
      case 'remove':
        return t('warehouse.history.actionRemove');
      default:
        return actionType;
    }
  };

  if (!localLoading && !localId) {
    return (
      <div className="warehouse-history">
        <Card variant="elevated">
          <p>{t('warehouse.dashboard.noLocalAssigned')}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="warehouse-history">
      <div className="warehouse-history__header">
        <h2 className="warehouse-history__title">{t('warehouse.history.title')}</h2>
        {availableLocals.length > 0 && (
          <SearchableSelect<number>
            className="warehouse-history__local-select"
            options={availableLocals.map((local) => ({ value: local.id, label: local.name }))}
            value={localId}
            onChange={(value) => setLocalId(value)}
          />
        )}
      </div>

      {error && <div className="warehouse-history__error">{error}</div>}

      {logs.length === 0 && !loading ? (
        <Card variant="elevated">
          <p>{t('warehouse.history.empty')}</p>
        </Card>
      ) : (
        <div className="warehouse-history__table-wrapper">
          <table className="warehouse-history__table">
            <thead>
              <tr>
                <th>{t('warehouse.history.date')}</th>
                <th>{t('warehouse.history.product')}</th>
                <th>{t('warehouse.history.action')}</th>
                <th>{t('warehouse.history.quantity')}</th>
                <th>{t('warehouse.history.note')}</th>
                <th>{t('warehouse.history.user')}</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td>{log.created_at ? formatRelativeTime(log.created_at) : '—'}</td>
                  <td>{log.product_name || '—'}</td>
                  <td>
                    <span className={`warehouse-history__action warehouse-history__action--${log.action_type}`}>
                      {actionLabel(log.action_type)}
                    </span>
                  </td>
                  <td>{log.quantity}</td>
                  <td>{log.note || '—'}</td>
                  <td>{log.user_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {logs.length < total && (
        <div className="warehouse-history__load-more">
          <Button variant="outline" size="sm" onClick={() => setOffset(logs.length)} disabled={loading} loading={loading}>
            {t('common.loadMore', { defaultValue: 'Cargar más' })}
          </Button>
        </div>
      )}
    </div>
  );
};

export default WarehouseHistory;
